/**
 * Pane-breedte zelf zetten (UX-review 17 oktober 2026, besluit Asil: eerst in ToD). Regels in paneWidthRules.ts.
 *
 *   usePaneWidth("tod", { preferred: 440, wide: 600 }, viewerOpen);
 *
 * Werkt alleen waar TaskPaneApi 1.1 bestaat (Excel Windows vanaf versie 2507, Mac vanaf 16.100, web 350–500 px);
 * elders doet de hook niets. Buiten de grenzen van het platform verandert Excel de breedte niet en geeft geen fout.
 * Wat de gebruiker zelf sleept, wordt per pane onthouden in localStorage (`agtt.paneWidth.<pane>`).
 */
import * as React from "react";
import {
  ExpectedResize,
  PaneWidthMemory,
  classifyResize,
  openTarget,
  rememberable,
  restoreTarget,
  widenTarget,
} from "./paneWidthRules";

interface TaskPaneApi {
  setWidth(width: number): void;
}

/** setWidth van Office, of null als dit Excel het niet kent. */
export function taskPaneApi(): TaskPaneApi | null {
  try {
    const office = (globalThis as unknown as { Office?: any }).Office;
    if (!office?.context?.requirements?.isSetSupported?.("TaskPaneApi", "1.1")) return null;
    const tp = office.extensionLifeCycle?.taskpane;
    return tp && typeof tp.setWidth === "function" ? (tp as TaskPaneApi) : null;
  } catch {
    return null;
  }
}

const KEY = (pane: string): string => `agtt.paneWidth.${pane}`;
const SCALE_KEY = "agtt.paneWidth.scale";

function loadMemory(pane: string): PaneWidthMemory {
  try {
    const raw = window.localStorage.getItem(KEY(pane));
    const m = raw ? (JSON.parse(raw) as PaneWidthMemory) : {};
    return { base: rememberable(m.base), wide: rememberable(m.wide) };
  } catch {
    return {};
  }
}
function saveMemory(pane: string, m: PaneWidthMemory): void {
  try {
    window.localStorage.setItem(KEY(pane), JSON.stringify(m));
  } catch {
    /* privévenster of geblokkeerde opslag: dan onthouden we niets */
  }
}
function loadScale(): number {
  try {
    const s = Number(window.localStorage.getItem(SCALE_KEY));
    return s > 0.5 && s < 4 ? s : 1;
  } catch {
    return 1;
  }
}

export interface PaneWidthOptions {
  /** Breedte bij openen (CSS-pixels). */
  preferred: number;
  /** Breedte in de brede stand, bijvoorbeeld met de bewijsviewer open. */
  wide?: number;
}

export function usePaneWidth(pane: string, opts: PaneWidthOptions, wideActive = false): void {
  const state = React.useRef({
    api: null as TaskPaneApi | null,
    expected: null as ExpectedResize | null,
    scale: 1,
    wide: false,
    before: 0,
    corrected: false,
  });

  const request = React.useCallback((css: number): void => {
    const s = state.current;
    if (!s.api) return;
    s.expected = { css, from: window.innerWidth, at: Date.now() };
    try {
      s.api.setWidth(Math.round(css * s.scale));
    } catch {
      /* platform weigert: dan blijft de breedte zoals hij is */
    }
  }, []);

  // Bij openen: voorkeur of eigen keuze; daarna luisteren naar resizes (eigen of van de gebruiker).
  React.useEffect(() => {
    const s = state.current;
    s.api = taskPaneApi();
    if (!s.api) return undefined;
    s.scale = loadScale();
    const target = openTarget(opts.preferred, loadMemory(pane), window.innerWidth);
    if (target !== null) request(target);

    let timer: number | undefined;
    const onResize = (): void => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        const w = window.innerWidth;
        const kind = classifyResize(
          w,
          s.expected,
          Date.now(),
          window.devicePixelRatio || 1,
          s.scale
        );
        if (kind === "own") return;
        if (kind === "scaled" && s.expected && !s.corrected) {
          // setWidth rekende in schermpixels: onthouden en één keer opnieuw vragen.
          s.corrected = true;
          s.scale = window.devicePixelRatio || 1;
          try {
            window.localStorage.setItem(SCALE_KEY, String(s.scale));
          } catch {
            /* niets */
          }
          request(s.expected.css);
          return;
        }
        s.expected = null;
        const m = loadMemory(pane);
        const keep = rememberable(w);
        if (keep === undefined) return;
        saveMemory(pane, s.wide ? { ...m, wide: keep } : { ...m, base: keep });
      }, 350);
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.clearTimeout(timer);
    };
    // Alleen bij openen van de pane; de voorkeur verandert niet tijdens het werk.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pane]);

  // Brede stand aan of uit (bijvoorbeeld de bewijsviewer).
  React.useEffect(() => {
    const s = state.current;
    if (!s.api || !opts.wide) return;
    if (wideActive && !s.wide) {
      s.wide = true;
      s.before = window.innerWidth;
      const target = widenTarget(opts.wide, loadMemory(pane), window.innerWidth);
      if (target !== null) request(target);
    } else if (!wideActive && s.wide) {
      s.wide = false;
      const target = restoreTarget(s.before, loadMemory(pane), window.innerWidth);
      if (target !== null) request(target);
    }
  }, [wideActive, opts.wide, pane, request]);
}
